import { HUB_EVENTS, hubEmitter } from '@/core/realtime';
import { type MessageListData, messageListKeys } from '@/features/message-list';
import { messageKeys } from '@/features/messages';
import type { PinnedMessage } from '@/features/messages/models/message-pinned';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

export const useMessagePinnedEvent = () => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const handleMessagePinned = ({ conversationId, messageId, pinnedAt, pinnedBy }: PinnedMessage) => {
      queryClient.setQueryData<MessageListData>(messageListKeys.conversation(conversationId), (data) => {
        if (!data) return data;

        const updatedPages = data.pages.map((page) =>
          page.map((msg) => (msg.messageId === messageId ? { ...msg, pinned: true, pinnedAt, pinnedBy } : msg))
        );

        return { pages: updatedPages, pageParams: data.pageParams };
      });
    };

    const handleMessageUnpinned = ({ conversationId, messageId }: PinnedMessage) => {
      queryClient.invalidateQueries({ queryKey: messageKeys.listPinned(conversationId) });

      // Update msg.pinned = false in message list cache
      queryClient.setQueryData<MessageListData>(messageListKeys.conversation(conversationId), (data) => {
        if (!data) return data;

        const updatedPages = data.pages.map((page) =>
          page.map((msg) =>
            msg.messageId === messageId ? { ...msg, pinned: false, pinnedAt: null, pinnedBy: null } : msg
          )
        );

        return { pages: updatedPages, pageParams: data.pageParams };
      });
    };

    hubEmitter.on(HUB_EVENTS.MESSAGE.PINNED, handleMessagePinned);
    hubEmitter.on(HUB_EVENTS.MESSAGE.UNPINNED, handleMessageUnpinned);

    return () => {
      hubEmitter.off(HUB_EVENTS.MESSAGE.PINNED, handleMessagePinned);
      hubEmitter.off(HUB_EVENTS.MESSAGE.UNPINNED, handleMessageUnpinned);
    };
  }, [queryClient]);
};
